import React, { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { updateStock, deleteProduct, updateMinStock, updateProductDetails, TRANSACTION_TYPES } from '../utils/stockUtils';
import AddProductModal from '../components/AddProductModal';
import ProductHistory from '../components/ProductHistory';
import * as XLSX from 'xlsx';
import printTable from '../utils/printUtils';


const ROLE_TRANSACTIONS = {
  admin:   ['restock', 'sale', 'writeoff', 'return', 'adjustment'],
  manager: ['restock', 'sale', 'writeoff', 'return', 'adjustment'],
};

function EditProductModal({ product, orgId, onClose }) {
  const [name, setName]         = useState(product.name);
  const [group, setGroup]       = useState(product.group);
  const [moq, setMoq]           = useState(product.moq);
  const [minStock, setMinStock] = useState(product.minStock ?? '');
  const [costPrice, setCostPrice] = useState(product.costPrice ?? '');
  const [supplier, setSupplier]   = useState(product.supplier ?? '');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleSave = async () => {
    if (!name.trim() || !group.trim()) { setError('Name and group are required.'); return; }
    if (isNaN(moq) || Number(moq) < 0) { setError('MOQ must be a valid non-negative number.'); return; }
    if (minStock !== '' && (isNaN(minStock) || Number(minStock) < 0)) {
      setError('Min Stock must be a valid non-negative number.'); return;
    }
    if (costPrice !== '' && (isNaN(costPrice) || Number(costPrice) < 0)) {
      setError('Cost Price must be a valid non-negative number.'); return;
    }
    setLoading(true); setError('');
    try {
      const updates = {
        name: name.trim(),
        group: group.trim(),
        moq: Number(moq),
      };
      updates.minStock = minStock !== '' ? Number(minStock) : null;
      updates.costPrice = costPrice !== '' ? Number(costPrice) : null;
      updates.supplier = supplier.trim() || null;

await updateProductDetails(product.id, updates, orgId);
      onClose();
    } catch (err) {
      setError('Failed to update product. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const inventoryValue = costPrice !== '' && !isNaN(costPrice)
    ? (Number(costPrice) * product.stock).toLocaleString('en-IN')
    : null;

  return (
    <div style={editStyles.overlay}>
      <div style={editStyles.modal}>
        <div style={editStyles.header}>
          <div>
            <h2 style={editStyles.title}>Edit Product</h2>
            <p style={editStyles.subtitle}>Stock value cannot be changed here — use transactions</p>
          </div>
          <button onClick={onClose} style={editStyles.closeBtn}>✕</button>
        </div>

        {error && <div style={editStyles.error}>{error}</div>}

        <div style={editStyles.sectionLabel}>Basic Details</div>
        {[
          { label: 'Product Name',                 value: name,  set: setName,  type: 'text',   placeholder: 'e.g. Basmati Rice 5kg' },
          { label: 'Group / Category',             value: group, set: setGroup, type: 'text',   placeholder: 'e.g. Grains' },
          { label: 'MOQ (supplier minimum order)', value: moq,   set: setMoq,   type: 'number', placeholder: '50' },
          { label: 'Min Stock (low-stock threshold)', value: minStock, set: setMinStock, type: 'number', placeholder: 'e.g. 20 (optional)' },
        ].map(field => (
          <div key={field.label} style={editStyles.field}>
            <label style={editStyles.label}>{field.label}</label>
            <input
              type={field.type}
              value={field.value}
              onChange={e => field.set(e.target.value)}
              placeholder={field.placeholder}
              style={editStyles.input}
            />
          </div>
        ))}

        <div style={editStyles.sectionLabel}>Sourcing & Value</div>
        {[
          { label: 'Cost Price (₹ per unit)', value: costPrice, set: setCostPrice, type: 'number', placeholder: 'e.g. 250.00 (optional)' },
          { label: 'Supplier Name',           value: supplier,  set: setSupplier,  type: 'text',   placeholder: 'e.g. Reliance Fresh Wholesale (optional)' },
        ].map(field => (
          <div key={field.label} style={editStyles.field}>
            <label style={editStyles.label}>{field.label}</label>
            <input
              type={field.type}
              value={field.value}
              onChange={e => field.set(e.target.value)}
              placeholder={field.placeholder}
              style={editStyles.input}
            />
          </div>
        ))}

        <div style={editStyles.currentStock}>
          <span style={editStyles.currentStockLabel}>Current Stock</span>
          <span style={editStyles.currentStockValue}>{product.stock}</span>
          {inventoryValue && (
            <span style={editStyles.inventoryValueBadge}>≈ ₹{inventoryValue} value</span>
          )}
          <span style={editStyles.currentStockHint}>use a transaction to change this</span>
        </div>

        <div style={editStyles.actions}>
          <button onClick={onClose} style={editStyles.cancelBtn}>Cancel</button>
          <button onClick={handleSave} disabled={loading} style={loading ? editStyles.saveBtnDisabled : editStyles.saveBtn}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

const editStyles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modal: { backgroundColor: '#ffffff', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', margin: '0 16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  subtitle: { fontSize: '12px', color: '#94a3b8' },
  closeBtn: { background: 'none', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer', padding: '4px' },
  error: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' },
  sectionLabel: { fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', marginTop: '6px' },
  field: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', boxSizing: 'border-box' },
  currentStock: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  currentStockLabel: { fontSize: '13px', fontWeight: '600', color: '#64748b' },
  currentStockValue: { fontSize: '18px', fontWeight: '800', color: '#1e293b' },
  inventoryValueBadge: { fontSize: '12px', fontWeight: '600', color: '#16a34a', backgroundColor: '#f0fdf4', padding: '2px 8px', borderRadius: '6px' },
  currentStockHint: { fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' },
  actions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '9px 18px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#64748b', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  saveBtn: { padding: '9px 18px', backgroundColor: '#3b82f6', border: 'none', color: '#ffffff', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' },
  saveBtnDisabled: { padding: '9px 18px', backgroundColor: '#93c5fd', border: 'none', color: '#ffffff', borderRadius: '8px', fontSize: '13px', cursor: 'not-allowed' },
};

// ─── Notes modal — full screen overlay on mobile, popover on desktop ──────────
function NotesModal({ value, onChange, onClose, productName }) {
  return (
    <div style={notesModalStyles.overlay} onClick={onClose}>
      <div style={notesModalStyles.sheet} onClick={e => e.stopPropagation()}>
        <div style={notesModalStyles.header}>
          <div>
            <div style={notesModalStyles.title}>Transaction note</div>
            <div style={notesModalStyles.subtitle}>{productName}</div>
          </div>
          <button onClick={onClose} style={notesModalStyles.closeBtn}>✕</button>
        </div>
        <textarea
          autoFocus
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Optional note for this transaction… e.g. 'Delivered by Sharma supplier' or 'Spoilage due to rain'"
          style={notesModalStyles.textarea}
          maxLength={200}
          rows={4}
        />
        <div style={notesModalStyles.footer}>
          <span style={notesModalStyles.charCount}>{value.length}/200 characters</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            {value.trim() && (
              <button onClick={() => onChange('')} style={notesModalStyles.clearBtn}>Clear</button>
            )}
            <button onClick={onClose} style={notesModalStyles.doneBtn}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const notesModalStyles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(2px)' },
  sheet: { backgroundColor: '#ffffff', borderRadius: '16px 16px 0 0', padding: '20px', width: '100%', maxWidth: '560px', boxShadow: '0 -8px 32px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', borderBottom: 'none' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' },
  title: { fontSize: '15px', fontWeight: '700', color: '#1e293b' },
  subtitle: { fontSize: '12px', color: '#94a3b8', marginTop: '2px' },
  closeBtn: { background: 'none', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer', padding: '2px 6px', borderRadius: '6px' },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '10px 12px', fontSize: '14px', resize: 'none', backgroundColor: '#f8fafc', color: '#1e293b', outline: 'none', fontFamily: 'inherit', lineHeight: '1.5' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' },
  charCount: { fontSize: '12px', color: '#94a3b8' },
  clearBtn: { padding: '7px 14px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#64748b', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  doneBtn: { padding: '7px 18px', backgroundColor: '#f59e0b', border: 'none', color: '#fff', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

// ─── Notes trigger button (desktop inline popover) ────────────────────────────
function NotesPopover({ value, onChange, onClose }) {
  return (
    <div style={notesPopoverStyles.popover} onClick={e => e.stopPropagation()}>
      <textarea
        autoFocus
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Optional note for this transaction…"
        style={notesPopoverStyles.textarea}
        maxLength={200}
        rows={2}
      />
      <div style={notesPopoverStyles.footer}>
        <span style={notesPopoverStyles.charCount}>{value.length}/200</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          {value.trim() && (
            <button onClick={() => onChange('')} style={notesPopoverStyles.clearBtn}>Clear</button>
          )}
          <button onClick={onClose} style={notesPopoverStyles.doneBtn}>Done</button>
        </div>
      </div>
    </div>
  );
}

const notesPopoverStyles = {
  popover: { position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50, backgroundColor: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: '10px', padding: '10px', width: '260px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1px solid #fde68a', borderRadius: '6px', padding: '6px 8px', fontSize: '13px', resize: 'none', backgroundColor: '#ffffff', color: '#1e293b', outline: 'none', fontFamily: 'inherit' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' },
  charCount: { fontSize: '10px', color: '#92400e' },
  clearBtn: { padding: '3px 8px', backgroundColor: '#ffffff', border: '1px solid #fde68a', color: '#92400e', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' },
  doneBtn: { padding: '4px 12px', backgroundColor: '#f59e0b', border: 'none', color: '#fff', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' },
};

// ─── Note trigger button shared component ─────────────────────────────────────
// Renders as a labeled button; on mobile uses full-sheet modal, on desktop uses popover
function NoteButton({ productId, productName, noteVal, isMobile, openNotesFor, setOpenNotesFor, setNotes, notes }) {
  const hasNote = noteVal.trim().length > 0;

  const handleClick = (e) => {
    e.stopPropagation();
    setOpenNotesFor(openNotesFor === productId ? null : productId);
  };

  const isOpen = openNotesFor === productId;

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={handleClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: isMobile ? '9px 12px' : '7px 10px',
          border: '1.5px solid',
          borderRadius: '8px',
          fontSize: '13px',
          cursor: 'pointer',
          fontWeight: hasNote ? '600' : '400',
          backgroundColor: hasNote ? '#fffbeb' : '#f8fafc',
          borderColor: hasNote ? '#fde68a' : '#e2e8f0',
          color: hasNote ? '#92400e' : '#64748b',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          position: 'relative',
        }}
        title={hasNote ? 'Edit transaction note' : 'Add a note to this transaction'}
      >
        <span style={{ fontSize: '14px' }}>📝</span>
        <span style={{ fontSize: '12px' }}>{hasNote ? 'Note added' : 'Add note'}</span>
        {hasNote && (
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#f59e0b', border: '1.5px solid #fff', position: 'absolute', top: '-3px', right: '-3px' }} />
        )}
      </button>

      {/* Desktop: popover. Mobile: full sheet modal handled at page level */}
      {!isMobile && isOpen && (
        <NotesPopover
          value={noteVal}
          onChange={v => setNotes(prev => ({ ...prev, [productId]: v }))}
          onClose={() => setOpenNotesFor(null)}
        />
      )}
    </div>
  );
}

// ─── Mobile product card ───────────────────────────────────────────────────────
function MobileProductCard({
  product, index, txnType, activeTxn, canEdit, isAdmin,
  quantities, setQuantities, updating, errors, deleting,
  editingMinStock, setEditingMinStock, savingMinStock,
  handleKeyDown, handleMinStockKeyDown, cancelEditMinStock,
  startEditMinStock, handleDelete, isLow, inputRefs, qtyMin, qtyPlaceholder,
  setHistoryProduct, setEditProduct,
  notes, setNotes, openNotesFor, setOpenNotesFor,
}) {
  const low = isLow(product);
  const isEditingMs = product.id in editingMinStock;
  const noteVal = notes[product.id] || '';

  return (
    <div style={{
      ...mobileCardStyles.card,
      backgroundColor: low ? '#fff5f5' : '#ffffff',
      borderLeft: `4px solid ${low ? '#ef4444' : '#e2e8f0'}`,
    }}>
      {/* Top row: name + badges */}
      <div style={mobileCardStyles.topRow}>
        <div style={mobileCardStyles.nameBlock}>
          <span
            onClick={() => setHistoryProduct(product)}
            style={mobileCardStyles.productName}
          >
            {product.name}
          </span>
          <span style={mobileCardStyles.groupBadge}>{product.group}</span>
        </div>
        <div style={mobileCardStyles.stockBlock}>
          <span style={{
            ...mobileCardStyles.stockNumber,
            color: low ? '#ef4444' : '#16a34a',
            backgroundColor: low ? '#fef2f2' : '#f0fdf4',
          }}>
            {product.stock}
          </span>
          {low && <span style={mobileCardStyles.lowBadge}>LOW</span>}
        </div>
      </div>

      {/* Meta row: Min Stock / MOQ */}
      <div style={mobileCardStyles.metaRow}>
        <div style={mobileCardStyles.metaItem}>
          <span style={mobileCardStyles.metaLabel}>Min Stock</span>
          {isEditingMs ? (
            <input
              type="number"
              min="0"
              autoFocus
              value={editingMinStock[product.id]}
              onChange={e => setEditingMinStock(prev => ({ ...prev, [product.id]: e.target.value }))}
              onKeyDown={e => handleMinStockKeyDown(e, product)}
              onBlur={() => setTimeout(() => cancelEditMinStock(product.id), 150)}
              disabled={savingMinStock[product.id]}
              style={mobileCardStyles.minStockInput}
              placeholder="set"
            />
          ) : (
            <span
              onMouseDown={() => canEdit && startEditMinStock(product)}
              style={{ ...mobileCardStyles.metaValue, cursor: canEdit ? 'pointer' : 'default' }}
            >
              {product.minStock != null ? product.minStock : <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>not set</span>}
              {canEdit && <span style={{ fontSize: '10px', color: '#cbd5e1', marginLeft: '3px' }}>✎</span>}
            </span>
          )}
        </div>
        <div style={mobileCardStyles.metaItem}>
          <span style={mobileCardStyles.metaLabel}>MOQ</span>
          <span style={mobileCardStyles.metaValue}>{product.moq}</span>
        </div>
        {product.supplier && (
          <div style={mobileCardStyles.metaItem}>
            <span style={mobileCardStyles.metaLabel}>Supplier</span>
            <span style={{ ...mobileCardStyles.metaValue, maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={product.supplier}>
              {product.supplier}
            </span>
          </div>
        )}
        {canEdit && (
          <div style={mobileCardStyles.iconActions}>
            <button onClick={() => setEditProduct(product)} style={mobileCardStyles.iconBtn} title="Edit product">✎</button>
            {isAdmin && (
              <button
                onClick={() => handleDelete(product)}
                disabled={deleting[product.id]}
                style={deleting[product.id] ? mobileCardStyles.deleteBtnDisabled : mobileCardStyles.deleteBtn}
              >
                {deleting[product.id] ? '...' : '🗑'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Update row */}
      {canEdit && (
        <div style={mobileCardStyles.updateRow}>
          {/* Qty input */}
          <input
            ref={el => inputRefs.current[product.id] = el}
            type="number"
            min={qtyMin}
            placeholder={qtyPlaceholder}
            value={quantities[product.id] || ''}
            onChange={e => setQuantities(prev => ({ ...prev, [product.id]: e.target.value }))}
            onKeyDown={e => handleKeyDown(e, product, index)}
            style={{
              ...mobileCardStyles.qtyInput,
              borderColor: activeTxn ? activeTxn.color + '88' : '#e2e8f0',
            }}
            disabled={updating[product.id]}
          />
          {/* Note button — full width row on mobile */}
          <NoteButton
            productId={product.id}
            productName={product.name}
            noteVal={notes[product.id] || ''}
            isMobile={true}
            openNotesFor={openNotesFor}
            setOpenNotesFor={setOpenNotesFor}
            notes={notes}
            setNotes={setNotes}
          />
          {updating[product.id] && <span style={mobileCardStyles.savingText}>saving…</span>}
          {errors[product.id] && (
            <span style={mobileCardStyles.errorText}>{errors[product.id]}</span>
          )}
          <span style={mobileCardStyles.enterHint}>↵ Enter to save</span>
        </div>
      )}
    </div>
  );
}

const mobileCardStyles = {
  card: { borderRadius: '10px', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', backgroundColor: '#fff' },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  nameBlock: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, marginRight: '10px' },
  productName: { fontSize: '15px', fontWeight: '600', color: '#1e293b', cursor: 'pointer', borderBottom: '1px dashed #94a3b8', alignSelf: 'flex-start' },
  groupBadge: { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '500', alignSelf: 'flex-start' },
  stockBlock: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 },
  stockNumber: { padding: '4px 12px', borderRadius: '20px', fontSize: '18px', fontWeight: '800', display: 'inline-block' },
  lowBadge: { backgroundColor: '#fef2f2', color: '#ef4444', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', border: '1px solid #fecaca' },
  metaRow: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' },
  metaItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  metaLabel: { fontSize: '10px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  metaValue: { fontSize: '13px', color: '#374151', fontWeight: '500', display: 'inline-flex', alignItems: 'center' },
  minStockInput: { width: '72px', padding: '5px 7px', border: '1.5px solid #8b5cf6', borderRadius: '6px', fontSize: '13px', color: '#1e293b', backgroundColor: '#faf5ff', outline: 'none' },
  iconActions: { marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' },
  iconBtn: { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '15px', padding: '4px 6px', borderRadius: '6px' },
  deleteBtn: { padding: '5px 9px', backgroundColor: '#ffffff', border: '1.5px solid #fca5a5', color: '#ef4444', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  deleteBtnDisabled: { padding: '5px 9px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#94a3b8', borderRadius: '8px', fontSize: '13px', cursor: 'not-allowed' },
  updateRow: { display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '10px', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' },
  qtyInput: { width: '110px', padding: '10px 14px', backgroundColor: '#f8fafc', border: '2px solid', borderRadius: '8px', color: '#1e293b', fontSize: '16px', fontWeight: '600' },
  enterHint: { fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', width: '100%' },
  savingText: { color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' },
  errorText: { color: '#ef4444', fontSize: '12px', backgroundColor: '#fef2f2', padding: '3px 8px', borderRadius: '4px', width: '100%' },
};
// ──────────────────────────────────────────────────────────────────────────────

export default function StockPage() {
  const { currentUser, userRole, orgId } = useAuth();
  const [products, setProducts]         = useState([]);
  const [txnType, setTxnType]           = useState('sale');
  const [quantities, setQuantities]     = useState({});
  const [notes, setNotes]               = useState({});
  const [openNotesFor, setOpenNotesFor] = useState(null);
  const [errors, setErrors]             = useState({});
  const [updating, setUpdating]         = useState({});
  const [deleting, setDeleting]         = useState({});
  const [editingMinStock, setEditingMinStock] = useState({});
  const [savingMinStock, setSavingMinStock]   = useState({});
  const [editProduct, setEditProduct]   = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [filterGroups, setFilterGroups] = useState([]);
  const [sortConfig, setSortConfig]     = useState({ key: 'name', direction: 'asc' });
  const [search, setSearch]             = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isMobile, setIsMobile]         = useState(() => window.innerWidth < 640);
  const inputRefs = useRef({});

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, 'products'), where('orgId', '==', orgId), orderBy('name'));
    return onSnapshot(q, snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [orgId]);

  // Close notes popover on outside click (desktop)
  useEffect(() => {
    if (!openNotesFor) return;
    const handler = () => setOpenNotesFor(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openNotesFor]);

  const canEdit = userRole === 'admin' || userRole === 'manager';
  const isAdmin = userRole === 'admin';
  const availableTxns = ROLE_TRANSACTIONS[userRole] || [];
  const activeTxn = TRANSACTION_TYPES[txnType];
  const isLow = (p) => p.minStock != null ? p.stock < p.minStock : p.stock < p.moq;
  const lowStockCount = products.filter(isLow).length;

  const allGroups = [...new Set(products.map(p => p.group).filter(Boolean))];

  const toggleGroup = (g) => {
    setFilterGroups(prev =>
      prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]
    );
  };

  const filtered = products
    .filter(p => filterGroups.length === 0 || filterGroups.includes(p.group))
    .filter(p => p.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      if (a[sortConfig.key] < b[sortConfig.key]) return -1 * dir;
      if (a[sortConfig.key] > b[sortConfig.key]) return 1 * dir;
      return 0;
    });

  const handleSort = (key) => setSortConfig(prev =>
    prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
  );

  const handleKeyDown = async (e, product, index) => {
    if (e.key !== 'Enter') return;
    const qty = quantities[product.id];
    if (!qty) return;
    setUpdating(prev => ({ ...prev, [product.id]: true }));
    setErrors(prev => ({ ...prev, [product.id]: null }));
    try {
      const note = notes[product.id] || '';
      await updateStock(product, qty, txnType, currentUser, orgId, note);
      setQuantities(prev => ({ ...prev, [product.id]: '' }));
      setNotes(prev => ({ ...prev, [product.id]: '' }));
      setOpenNotesFor(null);
      const next = filtered[index + 1];
      if (next && inputRefs.current[next.id]) inputRefs.current[next.id].focus();
    } catch (err) {
      setErrors(prev => ({ ...prev, [product.id]: err.message }));
    } finally {
      setUpdating(prev => ({ ...prev, [product.id]: false }));
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    setDeleting(prev => ({ ...prev, [product.id]: true }));
    try {
      await deleteProduct(product, currentUser, orgId);
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    } finally {
      setDeleting(prev => ({ ...prev, [product.id]: false }));
    }
  };

  const startEditMinStock = (product) => {
    setEditingMinStock(prev => ({ ...prev, [product.id]: product.minStock ?? '' }));
  };

  const cancelEditMinStock = (productId) => {
    setEditingMinStock(prev => { const n = { ...prev }; delete n[productId]; return n; });
  };

  const handleMinStockKeyDown = async (e, product) => {
    if (e.key === 'Escape') { cancelEditMinStock(product.id); return; }
    if (e.key !== 'Enter') return;
    const val = editingMinStock[product.id];
    if (val === '' || isNaN(val) || Number(val) < 0) {
      cancelEditMinStock(product.id);
      return;
    }
    setSavingMinStock(prev => ({ ...prev, [product.id]: true }));
    try {
      await updateMinStock(product.id, val);
      cancelEditMinStock(product.id);
    } catch (err) {
      alert('Failed to update min stock: ' + err.message);
    } finally {
      setSavingMinStock(prev => ({ ...prev, [product.id]: false }));
    }
  };

  const exportInventory = () => {
    const rows = filtered.map(p => ({
      'Product Name': p.name, 'Group': p.group, 'Current Stock': p.stock,
      'Min Stock': p.minStock ?? '—', 'MOQ': p.moq,
      'Cost Price': p.costPrice != null ? p.costPrice : '—',
      'Inventory Value': p.costPrice != null ? (p.costPrice * p.stock) : '—',
      'Supplier': p.supplier ?? '—',
      'Status': isLow(p) ? 'LOW' : 'OK',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    XLSX.writeFile(wb, 'inventory-snapshot.xlsx');
  };
  const printInventory = () => {
  printTable({
    title: 'Inventory Snapshot',
    subtitle: `${filtered.length} products${lowStockCount > 0 ? ` · ${lowStockCount} below min stock` : ''}`,
    columns: ['Product Name', 'Group', 'Current Stock', 'Min Stock', 'MOQ', 'Cost Price', 'Inventory Value', 'Supplier', 'Status'],
    rows: filtered.map(p => {
      const low = isLow(p);
      const value = p.costPrice != null ? p.costPrice * p.stock : null;
      return [
        p.name,
        p.group || '—',
        { value: p.stock, style: `font-weight:700;color:${low ? '#ef4444' : '#16a34a'};` },
        p.minStock ?? '—',
        p.moq,
        p.costPrice != null ? `₹${p.costPrice}` : '—',
        value != null ? `₹${value.toLocaleString('en-IN')}` : '—',
        p.supplier || '—',
        { value: low ? 'LOW' : 'OK', style: `font-weight:700;color:${low ? '#dc2626' : '#16a34a'};` },
      ];
    }),
    styles: { colWidths: ['18%', '10%', '10%', '10%', '8%', '10%', '12%', '14%', '8%'] },
  });
};

  const sortArrow = (key) => sortConfig.key !== key ? ' ↕' : sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  const qtyPlaceholder = txnType === 'adjustment' ? '±qty' : 'qty';
  const qtyMin = txnType === 'adjustment' ? undefined : '1';

  // Shared card props to avoid repetition
  const sharedCardProps = {
    txnType, activeTxn, canEdit, isAdmin,
    quantities, setQuantities, updating, errors, deleting,
    editingMinStock, setEditingMinStock, savingMinStock,
    handleKeyDown, handleMinStockKeyDown, cancelEditMinStock,
    startEditMinStock, handleDelete, isLow, inputRefs, qtyMin, qtyPlaceholder,
    setHistoryProduct, setEditProduct,
    notes, setNotes, openNotesFor, setOpenNotesFor,
  };

  // Active note for mobile modal
  const mobileNoteProduct = isMobile && openNotesFor
    ? products.find(p => p.id === openNotesFor)
    : null;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Stock Update</h1>
          <p style={styles.subtitle}>
            {products.length} products
            {lowStockCount > 0 && <span style={styles.lowAlert}> · {lowStockCount} below min stock</span>}
          </p>
        </div>
        <div style={styles.headerActions}>
<button onClick={exportInventory} style={styles.exportBtn}>⬇ Export</button>
<button onClick={printInventory} style={styles.exportBtn}>🖨️ Print</button>          {isAdmin && (
            <button onClick={() => setShowAddModal(true)} style={styles.addProductBtn}>+ Add Product</button>
          )}
        </div>
      </div>

      {canEdit && (
        <div style={styles.txnSection}>
          <span style={styles.txnLabel}>Transaction type:</span>
          <div style={styles.txnButtons}>
            {availableTxns.map(type => {
              const t = TRANSACTION_TYPES[type];
              const isActive = txnType === type;
              return (
                <button key={type} onClick={() => setTxnType(type)} style={{
                  ...styles.txnBtn,
                  backgroundColor: isActive ? t.color : '#ffffff',
                  color: isActive ? '#ffffff' : '#64748b',
                  borderColor: isActive ? t.color : '#e2e8f0',
                  fontWeight: isActive ? '600' : '400',
                }}>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Active transaction banner — more prominent ── */}
      {canEdit && activeTxn && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '10px',
          border: `1.5px solid ${activeTxn.color}55`,
          fontSize: '13px',
          marginBottom: '16px',
          backgroundColor: activeTxn.bg,
          color: activeTxn.color,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
        }}>
          {/* Color swatch */}
          <div style={{ width: '4px', alignSelf: 'stretch', borderRadius: '2px', backgroundColor: activeTxn.color, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: '700', marginBottom: '2px', fontSize: '13px' }}>
              Mode: {activeTxn.label}
            </div>
            <div style={{ fontWeight: '400', fontSize: '12px', opacity: 0.85 }}>
              {activeTxn.description}.{' '}
              {txnType === 'adjustment'
                ? 'Enter a positive number to increase stock or a negative number to decrease.'
                : activeTxn.direction === 1
                ? 'Enter a quantity and press Enter to increase stock.'
                : 'Enter a quantity and press Enter to decrease stock.'}
              {' '}Use the 📝 Add note button to attach context to a transaction.
            </div>
          </div>
        </div>
      )}

      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={styles.search}
        />
        <div style={styles.groupFilters}>
          <button
            onClick={() => setFilterGroups([])}
            style={filterGroups.length === 0 ? styles.groupActive : styles.groupInactive}
          >
            All
          </button>
          {allGroups.map(g => (
            <button
              key={g}
              onClick={() => toggleGroup(g)}
              style={filterGroups.includes(g) ? styles.groupActive : styles.groupInactive}
            >
              {g}
            </button>
          ))}
        </div>
        {filterGroups.length > 0 && (
          <span style={styles.filterHint}>
            {filterGroups.length} group{filterGroups.length > 1 ? 's' : ''} selected
            · <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setFilterGroups([])}>clear</span>
          </span>
        )}
      </div>

      {/* ── Mobile: card list ── */}
      {isMobile ? (
        <div>
          {filtered.length === 0 ? (
            <div style={styles.emptyCard}>
              {products.length === 0 ? (
                <>
                  <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>No products yet</p>
                  <p style={{ fontSize: '13px', color: '#94a3b8' }}>Click "+ Add Product" to get started</p>
                </>
              ) : 'No products match your search'}
            </div>
          ) : (
            filtered.map((product, index) => (
              <MobileProductCard key={product.id} product={product} index={index} {...sharedCardProps} />
            ))
          )}
        </div>
      ) : (
        /* ── Desktop: table ── */
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {[
                  { key: 'name',     label: 'Product Name' },
                  { key: 'group',    label: 'Group' },
                  { key: 'stock',    label: 'Stock' },
                  { key: 'minStock', label: 'Min Stock' },
                  { key: 'moq',      label: 'MOQ' },
                ].map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)} style={styles.th}>
                    {col.label}{sortArrow(col.key)}
                  </th>
                ))}
                {canEdit && <th style={styles.th}>Update</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((product, index) => {
                const low = isLow(product);
                const isEditingMs = product.id in editingMinStock;
                const noteVal = notes[product.id] || '';
                return (
                  <tr key={product.id} style={{
                    ...styles.tr,
                    backgroundColor: low ? '#fff5f5' : '#ffffff',
                    borderLeft: low ? '3px solid #ef4444' : '3px solid transparent',
                  }}>
                    <td style={styles.tdName}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          onClick={() => setHistoryProduct(product)}
                          style={styles.productNameLink}
                          title="View stock history"
                        >
                          {product.name}
                        </span>
                        {canEdit && (
                          <button
                            onClick={() => setEditProduct(product)}
                            style={styles.editIconBtn}
                            title="Edit product details"
                          >
                            ✎
                          </button>
                        )}
                      </div>
                      {product.supplier && (
                        <div style={styles.supplierHint}>via {product.supplier}</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.groupBadge}>{product.group}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.stockNumber,
                        color: low ? '#ef4444' : '#16a34a',
                        backgroundColor: low ? '#fef2f2' : '#f0fdf4',
                      }}>
                        {product.stock}
                      </span>
                      {low && <span style={styles.lowBadge}>LOW</span>}
                    </td>

                    <td style={styles.td}>
                      {isEditingMs ? (
                        <input
                          type="number"
                          min="0"
                          autoFocus
                          value={editingMinStock[product.id]}
                          onChange={e => setEditingMinStock(prev => ({ ...prev, [product.id]: e.target.value }))}
                          onKeyDown={e => handleMinStockKeyDown(e, product)}
                          onBlur={() => {
                            setTimeout(() => cancelEditMinStock(product.id), 150);
                          }}
                          disabled={savingMinStock[product.id]}
                          style={styles.minStockInput}
                          placeholder="set threshold"
                        />
                      ) : (
                        <span
                          onMouseDown={() => canEdit && startEditMinStock(product)}
                          style={{ ...styles.minStockDisplay, cursor: canEdit ? 'pointer' : 'default' }}
                          title={canEdit ? 'Click to edit min stock threshold' : ''}
                        >
                          {product.minStock != null
                            ? product.minStock
                            : <span style={styles.unset}>not set</span>}
                          {canEdit && <span style={styles.editHint}> ✎</span>}
                        </span>
                      )}
                    </td>

                    <td style={{ ...styles.td, color: '#64748b' }}>{product.moq}</td>

                    {canEdit && (
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', flexWrap: 'wrap' }}>
                          {/* Qty input */}
                          <input
                            ref={el => inputRefs.current[product.id] = el}
                            type="number"
                            min={qtyMin}
                            placeholder={qtyPlaceholder}
                            value={quantities[product.id] || ''}
                            onChange={e => setQuantities(prev => ({ ...prev, [product.id]: e.target.value }))}
                            onKeyDown={e => handleKeyDown(e, product, index)}
                            style={{ ...styles.qtyInput, borderColor: activeTxn ? activeTxn.color + '88' : '#e2e8f0' }}
                            disabled={updating[product.id]}
                          />
                          {/* Note button — labeled */}
                          <NoteButton
                            productId={product.id}
                            productName={product.name}
                            noteVal={noteVal}
                            isMobile={false}
                            openNotesFor={openNotesFor}
                            setOpenNotesFor={setOpenNotesFor}
                            notes={notes}
                            setNotes={setNotes}
                          />
                          {/* Delete — separated visually with a divider */}
                          {isAdmin && (
                            <>
                              <div style={{ width: '1px', height: '24px', backgroundColor: '#e2e8f0', flexShrink: 0 }} />
                              <button
                                onClick={() => handleDelete(product)}
                                disabled={deleting[product.id]}
                                style={deleting[product.id] ? styles.deleteBtnDisabled : styles.deleteBtn}
                                title="Delete product"
                              >
                                {deleting[product.id] ? '...' : '🗑'}
                              </button>
                            </>
                          )}
                          {updating[product.id] && <span style={styles.updatingDot}>saving…</span>}
                          {errors[product.id] && <span style={styles.rowError}>{errors[product.id]}</span>}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="6" style={styles.emptyRow}>
                    {products.length === 0 ? (
                      <div>
                        <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>No products yet</p>
                        <p style={{ fontSize: '13px', color: '#94a3b8' }}>Click "+ Add Product" to get started</p>
                      </div>
                    ) : 'No products match your search'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile notes modal (full sheet) */}
      {isMobile && mobileNoteProduct && (
        <NotesModal
          value={notes[mobileNoteProduct.id] || ''}
          onChange={v => setNotes(prev => ({ ...prev, [mobileNoteProduct.id]: v }))}
          onClose={() => setOpenNotesFor(null)}
          productName={mobileNoteProduct.name}
        />
      )}

      {showAddModal && <AddProductModal onClose={() => setShowAddModal(false)} orgId={orgId} />}
{editProduct && <EditProductModal product={editProduct} orgId={orgId} onClose={() => setEditProduct(null)} />}
      {historyProduct && (
        <ProductHistory
          product={historyProduct}
          onClose={() => setHistoryProduct(null)}
        />
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: '#64748b' },
  lowAlert: { color: '#ef4444', fontWeight: '600' },
  headerActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  exportBtn: { padding: '8px 16px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  addProductBtn: { padding: '8px 16px', backgroundColor: '#3b82f6', border: 'none', color: '#ffffff', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' },
  txnSection: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' },
  txnLabel: { fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' },
  txnButtons: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  txnBtn: { padding: '7px 16px', borderRadius: '20px', border: '1.5px solid', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s ease' },
  filters: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' },
  search: { padding: '9px 14px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', width: '240px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  groupFilters: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  groupActive: { padding: '7px 16px', borderRadius: '20px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 2px 8px rgba(59,130,246,0.3)' },
  groupInactive: { padding: '7px 16px', borderRadius: '20px', border: '1.5px solid #e2e8f0', backgroundColor: '#ffffff', color: '#64748b', fontSize: '13px', cursor: 'pointer' },
  filterHint: { fontSize: '12px', color: '#94a3b8' },
  tableWrapper: { backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' },
  th: { padding: '13px 16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#f8fafc', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'all 0.15s ease' },
  tdName: { padding: '14px 16px', color: '#1e293b', fontSize: '14px', fontWeight: '500' },
  supplierHint: { fontSize: '11px', color: '#94a3b8', marginTop: '2px' },
  td: { padding: '14px 16px', color: '#374151', fontSize: '14px' },
  groupBadge: { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  stockNumber: { padding: '4px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: '700', display: 'inline-block' },
  lowBadge: { backgroundColor: '#fef2f2', color: '#ef4444', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', marginLeft: '8px', letterSpacing: '0.05em', border: '1px solid #fecaca' },
  editIconBtn: { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '13px', padding: '2px 4px', borderRadius: '4px', lineHeight: 1 },
  productNameLink: { color: '#1e293b', cursor: 'pointer', fontWeight: '500', textDecoration: 'none', borderBottom: '1px dashed #94a3b8' },
  minStockDisplay: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 6px', borderRadius: '6px' },
  minStockInput: { width: '90px', padding: '6px 8px', border: '1.5px solid #8b5cf6', borderRadius: '6px', fontSize: '13px', color: '#1e293b', backgroundColor: '#faf5ff', outline: 'none' },
  editHint: { fontSize: '11px', color: '#cbd5e1' },
  unset: { color: '#cbd5e1', fontSize: '13px', fontStyle: 'italic' },
  qtyInput: { width: '80px', padding: '7px 10px', backgroundColor: '#f8fafc', border: '1.5px solid', borderRadius: '8px', color: '#1e293b', fontSize: '14px', fontWeight: '600', transition: 'border-color 0.15s ease' },
  updatingDot: { color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' },
  rowError: { color: '#ef4444', fontSize: '12px', maxWidth: '180px', backgroundColor: '#fef2f2', padding: '3px 8px', borderRadius: '4px' },
  deleteBtn: { padding: '6px 10px', backgroundColor: '#ffffff', border: '1.5px solid #fca5a5', color: '#ef4444', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  deleteBtnDisabled: { padding: '6px 10px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#94a3b8', borderRadius: '8px', fontSize: '13px', cursor: 'not-allowed' },
  emptyRow: { padding: '60px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' },
  emptyCard: { padding: '60px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' },
};